'use client';

import { ArrowUpRight, History, MessageSquareText, X } from 'lucide-react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import {
  createContext,
  type FormEvent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useSWR from 'swr';
import {
  CONTEXT_VERSION,
  type WorkingContext,
} from '@/lib/chat/working-context';
import { stashAskContext } from '@/lib/context/ask-handoff';
import type { Chat } from '@/lib/types/database';
import { fetcher, generateUUID } from '@/lib/utils';

/**
 * Ask, in one place.
 *
 * Ask is reachable from the rail on every surface and from the Decision
 * Desk's own header pill, and both have to drive the same dialog. Rather than
 * each owning a copy of the state, the dialog and its Cmd+K binding live here
 * and are mounted once at the root (`app/layout.tsx`); everything that offers
 * Ask calls `useAsk().open()`.
 */
const AskContext = createContext<{ open: () => void }>({ open: () => {} });

export function useAsk() {
  return useContext(AskContext);
}

export function AskProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname() ?? '/';
  const [askOpen, setAskOpen] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setAskOpen(true);
      }
      if (event.key === 'Escape') setAskOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const value = useMemo(() => ({ open: () => setAskOpen(true) }), []);

  return (
    <AskContext.Provider value={value}>
      {children}
      {askOpen ? (
        <AskDialog pathname={pathname} onClose={() => setAskOpen(false)} />
      ) : null}
    </AskContext.Provider>
  );
}

function AskDialog({
  pathname,
  onClose,
}: {
  pathname: string;
  onClose: () => void;
}) {
  const router = useRouter();
  const [question, setQuestion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Same SWR key the Ask history rail uses, so the two share one cache entry
  // and one request (components/decision-desk/chat-history.tsx).
  const { data: history } = useSWR<Chat[]>('/api/history', fetcher, {
    revalidateOnFocus: false,
  });
  const recent = (history ?? []).slice(0, 4);

  function submitQuestion(value: string) {
    const prompt = value.trim();
    if (!prompt || submitting) return;
    setSubmitting(true);
    sessionStorage.setItem('chat-seed-prompt', prompt);
    const contextKey = stashAskContext(contextForPath(pathname, prompt));
    const conversationId = generateUUID();
    onClose();
    router.push(
      `/ask/${conversationId}?context_key=${encodeURIComponent(contextKey)}`,
    );
  }

  function ask(event: FormEvent) {
    event.preventDefault();
    submitQuestion(question);
  }

  const scope = originLabel(pathname);
  const scopeLabel = scope.toLowerCase();

  return (
    <div
      className="fixed inset-0 z-50 bg-foreground/35 p-3 backdrop-blur-[6px] sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Ask SixDegree"
    >
      <button
        type="button"
        aria-label="Close Ask"
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />
      <div className="relative mx-auto mt-[8vh] max-w-2xl overflow-hidden rounded-[28px] border border-border-subtle bg-background p-5 shadow-[0_32px_110px_rgba(38,30,18,0.32)] dark:shadow-[0_32px_110px_rgba(0,0,0,0.78)] sm:mt-[12vh] sm:p-7">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold -tracking-wide">Ask</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Ask anything about the data you can access. Your current{' '}
              {scopeLabel} provides context.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close Ask"
            className="grid size-9 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-surface-hover hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </div>
        <form
          onSubmit={ask}
          className="flex min-h-16 items-center gap-3 rounded-2xl border border-border-subtle bg-card px-4 shadow-[0_8px_30px_rgba(35,28,17,0.06)] transition-colors focus-within:border-border-strong dark:shadow-[0_8px_30px_rgba(0,0,0,0.25)]"
        >
          <MessageSquareText className="size-[18px] shrink-0 text-muted-foreground" />
          <input
            autoFocus
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            placeholder="Ask what changed, why it matters, or what would change…"
            className="min-w-0 flex-1 bg-transparent py-4 text-sm leading-6 text-foreground outline-none placeholder:text-tertiary dark:placeholder:text-muted-foreground"
          />
          <button
            type="submit"
            disabled={!question.trim() || submitting}
            aria-label="Ask question"
            className="grid size-9 shrink-0 place-items-center rounded-full bg-foreground text-background transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-25"
          >
            <ArrowUpRight className="size-4" strokeWidth={2.2} />
          </button>
        </form>
        {recent.length > 0 ? (
          <div className="mt-6 border-t border-border-subtle pt-4">
            <div className="mb-2 flex items-center justify-between gap-4">
              <p className="text-xs font-medium text-tertiary">
                Pick up where you left off
              </p>
              <Link
                href="/ask"
                onClick={onClose}
                className="text-xs font-medium text-muted-foreground underline-offset-2 hover:underline"
              >
                All conversations
              </Link>
            </div>
            {recent.map((chat) => {
              const title = chat.title.trim() || 'Untitled conversation';
              return (
                <Link
                  key={chat.id}
                  href={`/ask/${chat.id}`}
                  onClick={onClose}
                  title={title}
                  className="group flex w-full items-center gap-3 rounded-xl p-3 text-left text-sm leading-5 transition-colors hover:bg-surface-hover"
                >
                  <History
                    aria-hidden
                    className="size-4 shrink-0 text-tertiary"
                  />
                  <span className="min-w-0 flex-1 truncate">{title}</span>
                </Link>
              );
            })}
          </div>
        ) : null}
        <div className="mt-6 border-t border-border-subtle pt-4">
          <p className="mb-2 text-xs font-medium text-tertiary">
            Try a question
          </p>
          {/*
            Domain-neutral on purpose. These were "the forecast", "Commit
            claims" and "the largest judgment" — Revenue vocabulary on a
            surface every business function shares, and
            REVENUE_PRODUCT_SURFACE.md holds that the dashboard must not
            branch on a business-function name to render the grammar.
          */}
          {[
            'What needs attention today?',
            'What changed since last week?',
            'Where did this number come from?',
          ].map((prompt) => (
            <button
              key={prompt}
              type="button"
              onClick={() => submitQuestion(prompt)}
              disabled={submitting}
              className="group flex w-full items-center justify-between gap-4 rounded-xl p-3 text-left text-sm leading-5 transition-colors hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span>{prompt}</span>
              <ArrowUpRight className="size-4 shrink-0 text-tertiary transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between border-t border-border-subtle pt-4 text-xs text-tertiary">
          <span>Evidence and access rules stay attached</span>
          <span className="hidden sm:inline">Enter to ask · Esc to close</span>
        </div>
      </div>
    </div>
  );
}

export function contextForPath(
  pathname: string,
  intent: string,
): WorkingContext {
  const onConversation = pathname === '/ask' || pathname.startsWith('/chat/');
  const safePath = onConversation ? '/' : pathname;
  const agentMatch = pathname.match(/^\/agents\/([^/]+)(?:\/|$)/);

  const context: WorkingContext = {
    v: CONTEXT_VERSION,
    filters: [],
    subjects: [],
    evidence: [],
    recordIds: [],
    intent,
    route: safePath,
    origin: {
      label: originLabel(pathname),
      href: safePath,
      // Marks this as the global Ask bar (Cmd+K), not a question seeded from
      // a finding/entity/Exploration. The Ask → Explore handoff uses this to
      // decide whether a successful create_exploration result should
      // navigate the user there automatically (see lib/explore/exploration-handoff.ts).
      kind: 'ask-bar',
    },
    returnPath: {
      label: `Return to ${originLabel(safePath)}`,
      href: safePath,
    },
  };

  if (agentMatch) context.agentId = decodeURIComponent(agentMatch[1]);
  const customerMatch = pathname.match(/^\/customers\/([^/]+)\/?$/);
  const followUpMatch = pathname.match(/^\/follow-ups\/([^/]+)\/?$/);
  if (customerMatch) context.recordIds = [decodeURIComponent(customerMatch[1])];
  if (followUpMatch) {
    const followUpId = decodeURIComponent(followUpMatch[1]);
    context.evidence = [{ followUpId }];
    context.origin = {
      ...context.origin,
      label: 'Follow-Up',
      kind: 'follow-up',
      followUpId,
    };
  }

  return context;
}

function originLabel(pathname: string) {
  if (pathname.startsWith('/customers/')) return 'Customer story';
  if (pathname.startsWith('/follow-ups/')) return 'Follow-Up';
  if (pathname.startsWith('/approvals')) return 'Approvals';
  if (pathname === '/decisions') return 'Decision History';
  if (pathname.startsWith('/agents/')) return 'Agent';
  if (pathname === '/agents') return 'Agents';
  if (pathname.startsWith('/governance')) return 'Governance';
  return 'Home';
}
